import { useMemo } from 'react';

import { isToolReleased, type Product } from '../utils/get-products';
import { useGlobalAppCtxSelector } from '../xstate/app.xstate';

interface WithTools {
  conditionalProducts?: Product[];
}

/**
 * Hides projects whose only enabled tools are behind an off flag.
 *
 * Filtering the tools alone is not enough: a project integrated with nothing
 * but a flagged tool would still be listed, with an empty tools column and a
 * row that leads nowhere -- which tells the reader the tool exists. A project
 * that has other tools keeps showing, minus the hidden one.
 *
 * A project integrated with no tools at all is left alone, since there is
 * nothing being concealed.
 */
export function useProjectsWithReleasedTools<T extends WithTools>(
  projects: T[] | undefined
): T[] {
  const featureFlags = useGlobalAppCtxSelector((ctx) => ctx.featureFlags);

  return useMemo(
    () =>
      (projects ?? []).filter(({ conditionalProducts }) => {
        const integrated = (conditionalProducts ?? []).filter(
          ({ integrated: isIntegrated }) => isIntegrated
        );

        if (integrated.length === 0) return true;

        return integrated.some(({ name }) =>
          isToolReleased(name, featureFlags)
        );
      }),
    [projects, featureFlags]
  );
}
